import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
  TextField, RadioGroup, FormControlLabel, Radio,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  onChangeFieldAlterarSenhaAction,
  onFocusFieldAlterarSenhaAction,
  limpaDialogAlterarSenhaAction,
  setOpenDialogAlterarSenhaAction,
} from '../../../store/Login/login.store';
import { alterarSenhaAction } from '../../../store/Login/login.saga';
import { getSerComercial } from '../../../services/core/usuario/usuario.services';

function CampoSenha({
  label, campo, onChangeField, onFocusField, revelado, onToggleRevelar, disabled,
}) {
  return (
    <TextField
      fullWidth
      margin="dense"
      autoComplete="new-password"
      disabled={disabled}
      type={revelado ? 'text' : 'password'}
      name={campo.nome}
      label={label}
      value={campo.value}
      error={campo.error}
      helperText={campo.error ? campo.errorMessage : ''}
      onChange={onChangeField}
      onFocus={onFocusField}
      InputProps={{
        inputProps: { maxLength: 25 },
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={onToggleRevelar}
              edge="end"
              aria-label={revelado ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {revelado ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}
CampoSenha.propTypes = {
  label: PropTypes.string.isRequired,
  campo: PropTypes.shape({
    nome: PropTypes.string,
    value: PropTypes.string,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
  }).isRequired,
  onChangeField: PropTypes.func.isRequired,
  onFocusField: PropTypes.func.isRequired,
  revelado: PropTypes.bool.isRequired,
  onToggleRevelar: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
CampoSenha.defaultProps = { disabled: false };

/**
 * Dialogo de alterar senha, acionado pelo icone de Configuracoes do Topbar
 * do protótipo. Reaproveita por completo o fluxo real (Redux + saga +
 * service) já usado pelo Dashboard/Toolbar reais — nenhuma lógica de
 * negócio nova aqui, só a camada visual em MUI v5. Ver
 * docs/architecture/plano-topbar-configuracoes.md.
 *
 * Este diálogo chama o endpoint real de troca de senha
 * (POST /core/logins/alterar-senha) — ao testar/demonstrar o protótipo,
 * usar um usuário de teste dedicado, nunca a conta pessoal de quem
 * estiver logado.
 */
export default function AlterarSenhaDialog() {
  const dispatch = useDispatch();

  const open = useSelector(state => state.authentication.openDialogAlterarSenha);
  const senhaAtual = useSelector(state => state.authentication.alterarSenha.senhaAtual);
  const novaSenha = useSelector(state => state.authentication.alterarSenha.novaSenha);
  const confirmarNovaSenha = useSelector(state => state.authentication.alterarSenha.confirmarNovaSenha);
  const idUsuario = useSelector(state => state.authentication.userInformation?.idUsuario) || null;

  const [revelaSenhaAtual, setRevelaSenhaAtual] = useState(false);
  const [revelaNovaSenha, setRevelaNovaSenha] = useState(false);
  const [revelaConfirmarNovaSenha, setRevelaConfirmarNovaSenha] = useState(false);
  const [serComercial, setSerComercial] = useState(false);
  const [serSiteCliente, setSerSiteCliente] = useState(false);

  useEffect(() => {
    if (!open || !idUsuario) return;
    const { accessToken } = JSON.parse(localStorage.getItem('userData'));
    getSerComercial(idUsuario, accessToken)
      .then(response => setSerComercial(response.data))
      .catch(() => setSerComercial(false));
  }, [open, idUsuario]);

  function fechar() {
    dispatch(limpaDialogAlterarSenhaAction());
    dispatch(setOpenDialogAlterarSenhaAction(false));
    setSerSiteCliente(false);
  }

  function onChangeField(event) {
    const { name, value } = event.target;
    dispatch(onChangeFieldAlterarSenhaAction(name, value));
  }

  function onFocusField(event) {
    const { name } = event.target;
    dispatch(onFocusFieldAlterarSenhaAction(name));
  }

  function onChangeSerSiteCliente(event) {
    const valor = event.target.value === '1';
    setSerSiteCliente(valor);
    if (valor) {
      dispatch(onChangeFieldAlterarSenhaAction('senhaAtual', ''));
    }
  }

  function confirmar() {
    dispatch(alterarSenhaAction(
      senhaAtual.value,
      novaSenha.value,
      confirmarNovaSenha.value,
      serSiteCliente,
    ));
  }

  return (
    <Dialog open={open} onClose={fechar} fullWidth maxWidth="sm">
      <DialogTitle>Alterar Senha</DialogTitle>
      <DialogContent>
        <form autoComplete="off" onSubmit={event => event.preventDefault()}>
          {/* Isca para o autocomplete do navegador: sem um campo de usuario
              visivel perto dos campos de senha, o Chrome as vezes preenche
              um campo de texto qualquer da tela por tras do dialogo (ex.:
              "No Processo") com o nome do usuario logado. */}
          <input
            type="text"
            name="usuario"
            autoComplete="username"
            value=""
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            style={{ display: 'none' }}
          />
          {serComercial && (
            <RadioGroup
              row
              aria-label="Qual senha alterar"
              value={serSiteCliente ? '1' : '0'}
              onChange={onChangeSerSiteCliente}
            >
              <FormControlLabel value="0" control={<Radio />} label="Sua senha para o sistema" />
              <FormControlLabel value="1" control={<Radio />} label="Senha para acesso ao Site do Cliente" />
            </RadioGroup>
          )}
          {!serSiteCliente ? (
            <CampoSenha
              label="Senha Atual"
              campo={senhaAtual}
              onChangeField={onChangeField}
              onFocusField={onFocusField}
              revelado={revelaSenhaAtual}
              onToggleRevelar={() => setRevelaSenhaAtual(atual => !atual)}
            />
          ) : <div style={{ height: '62.65px' }} />}
          <CampoSenha
            label="Nova Senha"
            campo={novaSenha}
            onChangeField={onChangeField}
            onFocusField={onFocusField}
            revelado={revelaNovaSenha}
            onToggleRevelar={() => setRevelaNovaSenha(atual => !atual)}
          />
          <CampoSenha
            label="Confirmar Nova Senha"
            campo={confirmarNovaSenha}
            onChangeField={onChangeField}
            onFocusField={onFocusField}
            revelado={revelaConfirmarNovaSenha}
            onToggleRevelar={() => setRevelaConfirmarNovaSenha(atual => !atual)}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={fechar}>Cancelar</Button>
        <Button onClick={confirmar} variant="contained">Confirmar</Button>
      </DialogActions>
    </Dialog>
  );
}
